/** Minimal plain-text editor: a single ``<textarea>`` with a line-number
 *  gutter. The browser performs all bidirectional layout and caret
 *  hit-testing natively, so mixed Persian/LaTeX content edits correctly
 *  with near-zero memory cost.
 *
 *  ``dir="auto"`` gives each paragraph (line) its own base direction:
 *  Persian lines render right-aligned RTL, LaTeX/ASCII lines stay LTR.
 *
 *  Soft wrapping is enabled, so a long source line occupies several
 *  visual rows. A hidden mirror div re-measures each line's rendered
 *  height (debounced, plus on resize) and every gutter number gets a
 *  row of matching height — numbers stay aligned with wrapped lines.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SaveStatus } from "../types";

interface Props {
  path: string;
  content: string;
  onContentChange: (content: string) => void;
  onSave: () => void;
  saveStatus: SaveStatus;
  onCursorChange?: (line: number, column: number) => void;
  targetLine?: { line: number; id: number } | null;
}

const LINE_HEIGHT = 22; // px — must match .ste-textarea line-height
const H_PADDING = 24;   // .ste-textarea left+right padding

type TextDir = "auto" | "rtl" | "ltr";

function readStoredDir(): TextDir {
  try {
    const saved = localStorage.getItem("ste_direction");
    return saved === "rtl" || saved === "ltr" ? saved : "auto";
  } catch {
    return "auto";
  }
}

export function SimpleTexEditor({
  path,
  content,
  onContentChange,
  onSave,
  onCursorChange,
  targetLine,
}: Props): JSX.Element {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [dir, setDir] = useState<TextDir>(readStoredDir);
  const onSaveRef = useRef(onSave);
  const onCursorChangeRef = useRef(onCursorChange);
  useEffect(() => {
    onSaveRef.current = onSave;
    onCursorChangeRef.current = onCursorChange;
  }, [onSave, onCursorChange]);

  const lines = useMemo(() => content.split("\n"), [content]);

  /** Measure each source line's wrapped height via the hidden mirror. */
  const measure = useCallback((): void => {
    const ta = taRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    mirror.style.width = `${Math.max(ta.clientWidth - H_PADDING, 40)}px`;
    mirror.textContent = "";
    const frag = document.createDocumentFragment();
    const divs: HTMLDivElement[] = [];
    for (const line of lines) {
      const d = document.createElement("div");
      // An empty line must still occupy one row.
      d.textContent = line === "" ? " " : line;
      frag.appendChild(d);
      divs.push(d);
    }
    mirror.appendChild(frag);
    setRowHeights(divs.map((d) => Math.max(d.offsetHeight, LINE_HEIGHT)));
  }, [lines]);

  // Debounced re-measure on content change + immediate measure on resize.
  useEffect(() => {
    const t = window.setTimeout(measure, 120);
    return () => window.clearTimeout(t);
  }, [measure]);
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    const ro = new ResizeObserver(measure);
    ro.observe(ta);
    return () => ro.disconnect();
  }, [measure]);

  /** Report caret position as 1-based line/column. */
  const reportCursor = useCallback((): void => {
    const ta = taRef.current;
    if (!ta || !onCursorChangeRef.current) return;
    const pos = ta.selectionStart;
    const before = ta.value.slice(0, pos);
    const line = before.split("\n").length;
    const col = pos - before.lastIndexOf("\n");
    onCursorChangeRef.current(line, col);
  }, []);

  const onScroll = useCallback((): void => {
    const ta = taRef.current;
    if (ta && gutterRef.current) {
      gutterRef.current.scrollTop = ta.scrollTop;
    }
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSaveRef.current();
      }
    },
    [],
  );

  // Jump to a line requested by the preview or problem panel.
  useEffect(() => {
    if (!targetLine) return;
    const ta = taRef.current;
    if (!ta) return;
    const lineIdx = Math.min(Math.max(targetLine.line - 1, 0), lines.length - 1);
    let offset = 0;
    for (let i = 0; i < lineIdx; i++) offset += lines[i].length + 1;
    ta.focus();
    ta.setSelectionRange(offset, offset);
    const top = rowHeights
      .slice(0, lineIdx)
      .reduce((sum, h) => sum + h, 0);
    ta.scrollTop = Math.max(0, top - 3 * LINE_HEIGHT);
    if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
    reportCursor();
  }, [targetLine, lines, rowHeights, reportCursor]);

  /** Pick a direction; clicking the active one returns to auto. */
  const pickDir = (next: Exclude<TextDir, "auto">): void => {
    setDir((prev) => {
      const value = prev === next ? "auto" : next;
      try {
        localStorage.setItem("ste_direction", value);
      } catch {
        /* ignore */
      }
      return value;
    });
  };

  return (
    <div className="ste">
      <div className="ste-toolbar">
        <button
          type="button"
          className={`ste-dir-btn${dir === "ltr" ? " active" : ""}`}
          title="چپ‌چین کردن همه خط‌ها"
          onClick={() => pickDir("ltr")}
        >
          ⇤ LTR چپ‌چین
        </button>
        <button
          type="button"
          className={`ste-dir-btn${dir === "rtl" ? " active" : ""}`}
          title="راست‌چین کردن همه خط‌ها"
          onClick={() => pickDir("rtl")}
        >
          RTL راست‌چین ⇥
        </button>
        <span className="ste-dir-hint">
          {dir === "auto" ? "خودکار — هر خط خودش جهت می‌گیرد" : ""}
        </span>
      </div>
      <div className="ste-body">
        <div ref={gutterRef} className="ste-gutter" aria-hidden="true">
          {lines.map((_, i) => (
            <div
              key={i}
              className="ste-gutter-row"
              style={{ height: rowHeights[i] ?? LINE_HEIGHT }}
            >
              {i + 1}
            </div>
          ))}
        </div>
        <textarea
          ref={taRef}
          className="ste-textarea"
          dir={dir}
          wrap="soft"
          spellCheck={false}
          aria-label={`Text editor for ${path}`}
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          onKeyDown={onKeyDown}
          onScroll={onScroll}
          onSelect={reportCursor}
        />
      </div>
      <div ref={mirrorRef} className="ste-mirror" aria-hidden="true" />
    </div>
  );
}
